<script setup>
import {
  ref,
  computed,
  inject,
  watch,
} from 'vue';
import usuarios from '@/api/usuarios';
import ModalSimple from '@/components/ModalSimple.vue';
import DropdownCifra from '@/components/DropdownCifra.vue';
import humanEnumName from '../../../libs/i18n';
import { dateToLocaleDateString } from '../../../libs/dates';
import IconoAsistencia from '../../../components/cifra/iconos/IconoAsistencia.vue';
import EstadisticaAsistencia from './EstadisticaAsistencia.vue';

const NOTIFICAR_DEFECTO = ['ausencia', 'retraso', 'expulsion', 'retraso_expulsion'];
const TIPOS_OBSERVACIONES = ['ausencia_justificada', 'expulsion', 'retraso_justificado'];
const TIPOS_RETRASO = ['retraso', 'retraso_justificado', 'retraso_expulsion'];
const ICONOS_APLICACION = {
  todo_el_dia: 'fa-calendar',
  esta_sesion: 'fa-clock',
  a_partir_de_esta_sesion: 'fa-forward',
};

const I18n = inject('I18n');

// Props
const props = defineProps({
  admin: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  alumnoAsistencia: { type: Object, required: true },
  infoCuaderno: { type: Object, required: true },
  tiposAsistencia: { type: Array, required: true },
  aplicacionesValidas: { type: Array, required: true },
});

// Reactive state
const tipo = ref(props.alumnoAsistencia.tipo);
const aplicacionInicial = props.alumnoAsistencia.aplicar_a || props.infoCuaderno.aplicacion_asistencia_defecto;
const aplicacion = ref(aplicacionInicial);
const observaciones = ref(props.alumnoAsistencia.justificacion?.observaciones
  || props.alumnoAsistencia.observaciones);
const ocultarObservaciones = ref(
  props.infoCuaderno.preferencias.preferencias.ocultar_observaciones,
);
const notificar = ref(false);
const notificable = ref(false);
const showModalNombre = ref(false);
const editado = ref(false);

// Computed properties
const tiposValidosEstadistica = computed(() => props.tiposAsistencia.filter((tipoAsistencia) => tipoAsistencia !== 'asistencia'));
const tieneJustificacion = computed(() => !!props.alumnoAsistencia.justificacion);
const puedeTenerObservaciones = computed(() => (
  TIPOS_OBSERVACIONES.includes(tipo.value)
  && !tieneJustificacion.value
));
const tooltipObservaciones = computed(() => {
  if (!puedeTenerObservaciones.value && observaciones.value && !tieneJustificacion.value) {
    return 'tooltip';
  }
  return undefined;
});
const titleObservaciones = computed(() => {
  if (!puedeTenerObservaciones.value && !tieneJustificacion.value) {
    return observaciones.value;
  }
  return undefined;
});
const puedeModificar = computed(() => !props.disabled && tipo.value !== 'privacion');

const puedeCambiarAplicacion = computed(() => {
  return !TIPOS_RETRASO.includes(tipo.value);
});

const iconoAplicacion = computed(() => iconoPorAplicacion(aplicacion.value));

const clasesIconoAplicacion = computed(() => [
  'fa',
  'fa-16x',
  'dropdown-toggle',
  iconoAplicacion.value,
  'clickable',
  'icono-aplicacion',
  `icono-aplicacion--${aplicacion.value}`,
  { disabled: !puedeCambiarAplicacion.value },
]);

const tooltipAplicacion = computed(() => {
  const label = I18n.t('aplicar_a');
  const valor = humanEnumName('alumno_asistencia', 'aplicar_a', aplicacion.value);
  return `${label}: ${valor}`;
});

// Watchers
watch(aplicacion, () => {
  editado.value = true;
});
watch(notificar, () => {
  editado.value = true;
});
watch(tipo, (value) => {
  editado.value = true;
  cambiarAplicacion(value);
  if (NOTIFICAR_DEFECTO.includes(value)) {
    notificar.value = props.infoCuaderno.notificar_asistencia_defecto !== false;
    notificable.value = true;
  } else {
    notificar.value = false;
    notificable.value = false;
  }
});
watch(observaciones, () => {
  editado.value = true;
});

// Methods
function cambiarAplicacion(tipoAsistencia) {
  if (TIPOS_RETRASO.includes(tipoAsistencia)) {
    aplicacion.value = 'esta_sesion';
  }
}

function cambiarTipo(tipoAsistencia) {
  if (!puedeModificar.value) {
    return;
  }
  tipo.value = tipoAsistencia;
  cambiarAplicacion(tipoAsistencia);
}

function mostrarChart() {
  // Al mostrar el modal se monta el componente de la estadística y se carga
  // solo automáticamente al montar
  showModalNombre.value = true;
}

function tooltipJustificado(justificacion) {
  return I18n.t('registrado_usuario_fecha', {
    usuario: usuarios.fullname(justificacion.creador, false),
    fecha: dateToLocaleDateString(justificacion.created_at),
  });
}

function tooltipPrivacion() {
  return I18n.t('privacion_asistencia', {
    desde: dateToLocaleDateString(props.alumnoAsistencia.fecha),
    hasta: dateToLocaleDateString(props.alumnoAsistencia.fecha_fin),
  });
}

function iconoPorAplicacion(valor) {
  return ICONOS_APLICACION[valor] || 'fa-check-circle';
}

function seleccionarAplicacion(valor) {
  if (!puedeCambiarAplicacion.value) {
    return;
  }
  aplicacion.value = valor;
}

function toJson() {
  return {
    id: props.alumnoAsistencia.id,
    tipo: tipo.value,
    aplicar_a: aplicacion.value,
    enviar_aviso: notificar.value,
    observaciones: observaciones.value,
    usuario_id: props.alumnoAsistencia.usuario_id,
    grupo_id: props.alumnoAsistencia.grupo_id || props.infoCuaderno.grupo?.id,
  };
}

defineExpose({
  editado,
  toJson,
});
</script>

<template>
  <div :class="{ editado: editado }">
    <div
      v-for="tipoAsistencia in props.tiposAsistencia"
      :key="tipoAsistencia"
      class="cc-col-icon"
    >
      <icono-asistencia
        :tipo="tipoAsistencia"
        :show-tooltip="false"
        class="fa-16x clickable"
        :class="{ active: tipoAsistencia === tipo }"
        @click="cambiarTipo(tipoAsistencia)"
      />
    </div>
    <div class="cc-col-icon">
      <DropdownCifra :disabled="!puedeCambiarAplicacion">
        <template #toggle="{ toggle }">
          <i
            v-tooltip="tooltipAplicacion"
            :class="clasesIconoAplicacion"
            @click="toggle"
          >
            <span class="caret"></span>
          </i>
        </template>
        <template #menu="{ close }">
          <li v-for="aplicacion_lista in props.aplicacionesValidas" :key="aplicacion_lista">
            <a @click.prevent="seleccionarAplicacion(aplicacion_lista); close()">
              <i
                :class="[
                  'fa',
                  iconoPorAplicacion(aplicacion_lista),
                  { 'icono-aplicacion': aplicacion_lista === aplicacion },
                ]"
              ></i>
              {{ humanEnumName('alumno_asistencia', 'aplicar_a', aplicacion_lista) }}
            </a>
          </li>
        </template>
      </DropdownCifra>
    </div>
    <div class="cc-col-icon">
      <input-checkbox-boolean ref="checkNotificar" v-model="notificar" :disabled="!notificable" />
    </div>
    <div class="cc-col-input">
      <div class="observaciones-wrapper">
        <i
          v-if="tipo == 'privacion'"
          v-tooltip="tooltipPrivacion()"
          class="far cfa-privacion"
        ></i>
        <i
          v-if="observaciones"
          v-tooltip="observaciones"
          class="far fa-comment"
        ></i>
        <i
          v-if="props.alumnoAsistencia.justificacion"
          v-tooltip="tooltipJustificado(props.alumnoAsistencia.justificacion)"
          class="far fa-user-edit"
        ></i>
        <input
          v-model="observaciones"
          :disabled="!puedeTenerObservaciones"
          class="observaciones form-control"
          :class="{ 'blurry': ocultarObservaciones }"
          :data-toggle="tooltipObservaciones"
          :title="titleObservaciones"
        />
      </div>
    </div>
    <div class="cc-col-icon">
      <i
        v-tooltip="I18n.t('estadistica_evaluacion')"
        class="far fa-chart-bar fa-16x clickable"
        @click="mostrarChart()"
      >
      </i>
      <ModalSimple v-model="showModalNombre">
        <estadistica-asistencia
          v-if="showModalNombre"
          ref="estadisticas"
          :admin="props.admin"
          :cuaderno-id="props.infoCuaderno.id"
          :alumno-id="props.alumnoAsistencia.usuario_id"
          :fecha="new Date(props.infoCuaderno.fecha)"
          :tipos-validos="tiposValidosEstadistica"
        />
      </ModalSimple>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "../../../../assets/stylesheets/cifra/vars/main.scss";

  i.fa:not(.active):not(.icono-aplicacion):before {
    color: $gris-disabled;
  }
  i.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  :deep(.pretty) {
    input[disabled] ~ * {
      opacity: 0.2;
    }
  }
  .editado {
    .cc-col-icon:first-child {
      border-left: 3px solid $cifra-color;
    }
  }
  .observaciones-wrapper {
    display: flex;
    align-items: center;

    > i {
      margin-right: 10px;
      font-size: 1.4em;
    }

    input.observaciones.blurry {
      filter: blur(0.3rem);
    }

    input.observaciones.blurry:hover {
      filter: none;
    }
  }
</style>
